import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ConfirmDelete,
  DonutChart,
  PopoverFilterMenu,
  AppIcon,
  PopoverFilterMenuItem,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Button, Box, Typography, ClickAwayListener, SxProps, Theme } from '@mui/material';

import { PLAYLIST_CARD_DRAWER_WIDTH } from '../constants/priorities.constants';
import { getPlaybookPath } from '../utils/playlist.utils';

const PLAYLIST_CARD_STRINGS = appStrings.modules.priorities.playlistDrawer;

type PlaylistViewDrawerHeaderProps = Readonly<{
  currentPlaylist: PriorityPlaylist;
  confirmDeleteOpen: boolean;
  setConfirmDeleteOpen: (open: boolean) => void;
  handleDeletePlaylist: () => void;
  sortAnchorEl: HTMLElement | null;
  setSortAnchorEl: (anchorEl: HTMLElement | null) => void;
  handleOpenNewTaskDialog: (playlist: PriorityPlaylist) => void;
  handleClose: () => void;
  percentageFilled: number;
  wrappedSortMenuItems: PopoverFilterMenuItem[];
  isReorderMode: boolean;
  handleToggleReorderMode: () => void;
}>;

export const PlaylistViewDrawerHeader = ({
  currentPlaylist,
  confirmDeleteOpen,
  setConfirmDeleteOpen,
  handleDeletePlaylist,
  sortAnchorEl,
  setSortAnchorEl,
  handleOpenNewTaskDialog,
  handleClose,
  percentageFilled,
  wrappedSortMenuItems,
  isReorderMode,
  handleToggleReorderMode,
}: PlaylistViewDrawerHeaderProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  return (
    <>
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={PLAYLIST_CARD_STRINGS.deletePlaylist}
        bodyText={PLAYLIST_CARD_STRINGS.deletePlaylistConfirmBody}
        handleDelete={handleDeletePlaylist}
      />

      {/* Sort Menu */}
      <PopoverFilterMenu
        anchorEl={sortAnchorEl}
        setAnchorEl={setSortAnchorEl}
        title={PLAYLIST_CARD_STRINGS.sortBy}
        categories={[
          {
            label: PLAYLIST_CARD_STRINGS.sortOptions,
            items: wrappedSortMenuItems, // use wrapped items
          },
        ]}
        singleLevel
      />

      <PopoverFilterMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        categories={[
          {
            label: PLAYLIST_CARD_STRINGS.actions,
            items: [
              {
                label: PLAYLIST_CARD_STRINGS.sortBy,
                value: 'sort',
                category: 'action',
                icon: 'FilterLines',
                rightIcon: 'ChevronRight',
                isSelected: false,
                onClick: () => setSortAnchorEl(anchorEl),
                closeOnClick: true,
              },
              {
                label: PLAYLIST_CARD_STRINGS.reorderPlaylist,
                value: 'reorder',
                category: 'action',
                icon: 'DotPoints02',
                isSelected: isReorderMode,
                onClick: handleToggleReorderMode,
                closeOnClick: true,
              },
              {
                label: PLAYLIST_CARD_STRINGS.deletePlaylist,
                value: 'delete',
                category: 'action',
                icon: 'Trash01',
                isSelected: false,
                onClick: () => setConfirmDeleteOpen(true),
                closeOnClick: true,
              },
            ],
          },
        ]}
        singleLevel
      />

      <Box sx={headerSx}>
        <DonutChart
          variant={{
            type: 'solo',
            percentageFilled,
          }}
          size={52}
          isTooltipEnabled={false}
        />

        <Box sx={titleContainerSx}>
          <Typography variant="text-xl-semibold" sx={titleSx}>
            {currentPlaylist.title}
          </Typography>
          <Typography variant="text-sm-regular" sx={descriptionSx}>
            {currentPlaylist.description}
          </Typography>
        </Box>

        <Box onClick={handleClose} sx={closeButtonSx}>
          <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
        </Box>
      </Box>

      <Box sx={drawerActionsRowSx}>
        <Box sx={drawerActionsContentSx}>
          <Button
            id="playlist-new-task-button"
            variant="contained"
            hierarchy="Secondary gray"
            size="md"
            fullWidth
            onClick={() => handleOpenNewTaskDialog(currentPlaylist)}
            startIcon={
              <AppIcon iconName="Plus" diameter="20px" color="Colors/Foreground/fg-primary" />
            }
          >
            {PLAYLIST_CARD_STRINGS.newTask}
          </Button>

          <Button
            id="playlist-open-playbook-button"
            variant="contained"
            hierarchy="Secondary gray"
            size="md"
            sx={openPlaybookButtonSx}
            onClick={() => {
              handleClose();
              navigate(getPlaybookPath(currentPlaylist.id));
            }}
            startIcon={
              <AppIcon iconName="Maximize01" diameter="20px" color="Colors/Foreground/fg-primary" />
            }
          >
            {PLAYLIST_CARD_STRINGS.openPlaybook}
          </Button>

          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Button
              id="playlist-menu-button"
              variant="contained"
              hierarchy="Secondary gray"
              icon="Only"
              size="md"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              startIcon={
                <AppIcon
                  iconName="DotsHorizontal"
                  diameter="20px"
                  color="Colors/Foreground/fg-primary"
                />
              }
            />
          </ClickAwayListener>
        </Box>
        {isReorderMode && (
          <Button
            id="playlist-done-reordering-btn"
            variant="contained"
            hierarchy="Primary"
            size="md"
            fullWidth
            onClick={handleToggleReorderMode}
            sx={doneReorderingButtonSx}
          >
            {PLAYLIST_CARD_STRINGS.doneReordering}
          </Button>
        )}
      </Box>
    </>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
  gap: backrs.spacing['spacing-lg'],
  marginBottom: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-sm'],
});

const openPlaybookButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const closeButtonSx: SxProps<Theme> = {
  cursor: 'pointer',
  marginLeft: 'auto',
};

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  flex: 1,
  minWidth: 0,
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` },
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 5,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: { sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px - (${backrs.spacing['spacing-7xl']}))` },
});

const drawerActionsContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  px: `calc(${backrs.spacing['spacing-2xl']} + ${backrs.spacing['spacing-xxs']})`,
  mx: `-${backrs.spacing['spacing-2xl']}`,
});

const drawerActionsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
  px: `calc(${backrs.spacing['spacing-2xl']} + ${backrs.spacing['spacing-xxs']})`,
  pb: backrs.spacing['spacing-xl'],
  mb: backrs.spacing['spacing-xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  mx: `-${backrs.spacing['spacing-xl']}`,
});

const doneReorderingButtonSx: SxProps<Theme> = ({ backrs }) => ({
  px: `${backrs.spacing['spacing-xl']}`,
});
